/**
 * The first thing a new firm sees on the dashboard: guidance instead of a bare
 * "No claims yet". Which card shows depends on the inbox scan (see
 * hooks/use-inbox-scan.ts for what the email-to-first-claim pipeline provides):
 *
 *   not connected -> connect Gmail, or add a claim manually
 *   scanning      -> progress
 *   found         -> the claim with what ClaimHive pulled out (once per user)
 *   none yet      -> add a claim manually, or connect another inbox
 *   error         -> reconnect, or add a claim manually
 *
 * Only users in new beta firms get the panel (see hooks/use-new-beta-firm.ts);
 * everyone else sees the normal dashboard and no first-run request is made.
 *
 * Once the firm has a claim and the reveal has been seen or dismissed, the
 * panel renders nothing and the normal dashboard takes over.
 */
import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useLocation } from 'wouter';
import NewClaimModal from '@/components/claims/modals/new-claim-modal';
import { FirstClaimFound } from '@/components/onboarding/first-claim-found';
import { InboxScanProgress } from '@/components/onboarding/inbox-scan-progress';
import { NewFirmEmptyState } from '@/components/onboarding/new-firm-empty-state';
import { useFirstRun } from '@/hooks/use-first-run';
import { useInboxScan } from '@/hooks/use-inbox-scan';
import { useIsNewBetaFirmUser } from '@/hooks/use-new-beta-firm';
import { usePermissions } from '@/hooks/use-permissions';
import { apiRequest } from '@/lib/query-client';

/** How many claims the signed-in user can see. Shared by the dashboard and the panel (one request). */
export function useClaimCount(enabled = true) {
  const query = useQuery<{ total?: number }>({
    queryKey: ['/api/claims', { limit: 1, offset: 0 }],
    enabled,
    staleTime: 0,
  });
  return { count: query.data?.total ?? 0, isLoading: query.isLoading, isError: query.isError };
}

/** Best effort: records the "first claim" step of the onboarding checklist. Never blocks or fails the UI. */
function markFirstClaimStep() {
  apiRequest('POST', '/api/onboarding/walkthrough', { step: 'firstClaim' }).catch(() => undefined);
}

interface FirstClaimPanelProps {
  onConnectGmail: () => void;
}

export function FirstClaimPanel({ onConnectGmail }: FirstClaimPanelProps) {
  const [, setLocation] = useLocation();
  const permissions = usePermissions();
  const [showNewClaim, setShowNewClaim] = useState(false);

  const isNewBetaFirmUser = useIsNewBetaFirmUser();
  // GET /api/inbox-scan/status needs the claims list's permission (claims.read_all or claims.read_assigned); without it
  // the route answers 403, so the scan is not asked for and the panel offers manual entry only.
  const canReadScanStatus = permissions.canReadAllClaims || permissions.canReadAssignedClaims;
  const canScan = isNewBetaFirmUser && permissions.canViewAssignedCommunications && canReadScanStatus;
  const canSeeClaims = isNewBetaFirmUser && permissions.canViewClaims;
  const claims = useClaimCount(canSeeClaims);
  const scan = useInboxScan({ enabled: canScan });
  const reveal = useFirstRun('first-claim-reveal', { enabled: canSeeClaims });

  if (!canSeeClaims || permissions.isLoading) return null;
  if (claims.isLoading || claims.isError || reveal.isLoading || (canScan && !scan.state)) return null;

  const state = scan.state;
  const openClaim = (claimId: string) => {
    reveal.complete();
    markFirstClaimStep();
    setLocation(`/claims/${claimId}`);
  };
  const newClaimModal = permissions.canCreateClaims ? (
    <NewClaimModal
      open={showNewClaim}
      onOpenChange={setShowNewClaim}
      onCreated={(id) => {
        markFirstClaimStep();
        setLocation(`/claims/${id}`);
      }}
    />
  ) : null;
  const addManually = permissions.canCreateClaims ? () => setShowNewClaim(true) : undefined;

  const showReveal =
    state?.status === 'found' && state.claimIds.length > 0 && !reveal.completed && !reveal.isError;
  if (showReveal && state) {
    return (
      <div data-testid="first-claim-panel">
        <FirstClaimFound
          claimId={state.claimIds[0]}
          foundCount={Math.max(state.foundCount, state.claimIds.length)}
          accountEmail={state.accountEmail}
          onOpenClaim={openClaim}
          onViewAllClaims={() => {
            reveal.complete();
            setLocation('/claims');
          }}
          onDismiss={reveal.complete}
        />
      </div>
    );
  }

  if (claims.count > 0) return null;

  if (state?.status === 'scanning') {
    return (
      <div data-testid="first-claim-panel">
        <InboxScanProgress state={state} onAddManually={addManually} />
        {newClaimModal}
      </div>
    );
  }

  const variant = !canScan
    ? 'manual_only'
    : state?.status === 'not_connected'
      ? 'not_connected'
      : state?.status === 'error'
        ? 'error'
        : 'none_yet';

  return (
    <div data-testid="first-claim-panel">
      <NewFirmEmptyState
        variant={variant}
        accountEmail={state?.accountEmail}
        onConnectGmail={canScan ? onConnectGmail : undefined}
        onAddManually={addManually}
      />
      {newClaimModal}
    </div>
  );
}
